import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Zap, Coins, ShieldCheck, Languages, Headset } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/seo/SEO";

const HERO_IMAGE = "https://images.unsplash.com/photo-1567072629554-20e689de2400";
const PRODUCER_IMAGE = "https://images.unsplash.com/photo-1669711517916-9c0767aaa08b";
const CANDIDATE_IMAGE = "https://images.pexels.com/photos/10922960/pexels-photo-10922960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CANDIDATE_FEATURES = ["pricingCandidatesFeature1", "pricingCandidatesFeature2", "pricingCandidatesFeature3", "pricingCandidatesFeature4", "pricingCandidatesFeature5"];
const PRODUCER_FEATURES = ["pricingProducersFeature1", "pricingProducersFeature2", "pricingProducersFeature3", "pricingProducersFeature4", "pricingProducersFeature5"];
const PREMIUM_FEATURES = ["pricingPremiumFeature1", "pricingPremiumFeature2", "pricingPremiumFeature3", "pricingPremiumFeature4", "pricingPremiumFeature5", "pricingPremiumFeature6"];
const CREDIT_PACKS = [
  { credits: 10, price: 49 },
  { credits: 25, price: 99 },
  { credits: 50, price: 179 },
  { credits: 100, price: 299 },
];

export default function LandingPage() {
  const { t } = useTranslation();

  return (
    <div>
      <SEO title="JobVinum | Recrutement saisonnier vitivinicole en Europe"
           description="Connectez producteurs viticoles et travailleurs saisonniers en Europe : offres d'emploi vendanges, taille et travaux de vigne, profils multilingues, mise en relation en quelques minutes." path="/" />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/85 to-[#FAFAFA]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#dde165]/25 text-[#2B2B2B] text-xs font-bold tracking-wide mb-6 animate-fade-in-up">
            {t("landing.heroBadge")}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2B2B2B] leading-none mb-6 animate-fade-in-up" style={{ animationDelay: "0.05s" }}>
            {t("landing.heroTitle")}
          </h1>
          <p className="text-base sm:text-lg text-[#2B2B2B]/70 max-w-2xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            {t("landing.heroSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
            <Button asChild size="lg" className="h-14 px-8 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] text-white font-bold text-base" data-testid="landing-cta-producer-button">
              <Link to="/register?role=producer">
                {t("landing.ctaProducer")} <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-14 px-8 rounded-xl border-black/10 font-bold text-base" data-testid="landing-cta-candidate-button">
              <Link to="/register?role=candidate">{t("landing.ctaCandidate")}</Link>
            </Button>
          </div>
          <Link to="/offers" className="inline-block mt-6 text-sm font-semibold text-[#4ba3b7] hover:underline" data-testid="landing-browse-offers-link">
            {t("landing.browseOffers")} &rarr;
          </Link>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 -mt-8 relative z-10" data-testid="landing-stats-section">
        <p className="text-center text-xs font-bold uppercase tracking-wider text-[#4ba3b7] mb-3">{t("landing.statsTitle")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {[
            { title: t("landing.stat1Title"), desc: t("landing.stat1Desc") },
            { title: t("landing.stat2Title"), desc: t("landing.stat2Desc") },
            { title: t("landing.stat3Title"), desc: t("landing.stat3Desc") },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 sm:p-8 text-center">
              <p className="font-heading text-2xl sm:text-3xl font-bold text-[#4ba3b7] mb-1">{s.title}</p>
              <p className="text-sm text-[#2B2B2B]/60">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mb-14">
          {t("landing.howItWorksTitle")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] overflow-hidden hover:-translate-y-1 transition-transform duration-200">
            <img src={PRODUCER_IMAGE} alt="" className="w-full h-48 object-cover" />
            <div className="p-6 sm:p-8">
              <h3 className="font-heading text-xl font-bold mb-4">{t("landing.forProducers")}</h3>
              <ol className="space-y-3">
                {["producerStep1", "producerStep2", "producerStep3", "producerStep4"].map((step, i) => (
                  <li key={step} className="flex items-center gap-3 text-sm">
                    <span className="h-6 w-6 rounded-full bg-[#4ba3b7] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    {t(`landing.${step}`)}
                  </li>
                ))}
              </ol>
              <Link to="/register?role=producer" className="inline-block mt-6 text-sm font-semibold text-[#4ba3b7] hover:underline" data-testid="landing-producer-learn-more-link">
                {t("landing.learnMore")} &rarr;
              </Link>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] overflow-hidden hover:-translate-y-1 transition-transform duration-200">
            <img src={CANDIDATE_IMAGE} alt="" className="w-full h-48 object-cover" />
            <div className="p-6 sm:p-8">
              <h3 className="font-heading text-xl font-bold mb-4">{t("landing.forCandidates")}</h3>
              <ol className="space-y-3">
                {["candidateStep1", "candidateStep2", "candidateStep3", "candidateStep4"].map((step, i) => (
                  <li key={step} className="flex items-center gap-3 text-sm">
                    <span className="h-6 w-6 rounded-full bg-[#dde165] text-[#2B2B2B] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    {t(`landing.${step}`)}
                  </li>
                ))}
              </ol>
              <Link to="/register?role=candidate" className="inline-block mt-6 text-sm font-semibold text-[#4ba3b7] hover:underline" data-testid="landing-candidate-learn-more-link">
                {t("landing.learnMore")} &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURETTES */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-20 border-t border-black/5" data-testid="landing-featurettes-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { Icon: ShieldCheck, title: t("landing.featureVerifiedTitle"), desc: t("landing.featureVerifiedDesc") },
            { Icon: Languages, title: t("landing.featureLanguageTitle"), desc: t("landing.featureLanguageDesc") },
            { Icon: Headset, title: t("landing.featureSupportTitle"), desc: t("landing.featureSupportDesc") },
          ].map((f, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-3" data-testid={`landing-featurette-${i}`}>
              <div className="h-14 w-14 rounded-2xl bg-[#4ba3b7]/10 flex items-center justify-center">
                <f.Icon className="h-6 w-6 text-[#4ba3b7]" />
              </div>
              <h3 className="font-heading text-lg font-bold">{f.title}</h3>
              <p className="text-sm text-[#2B2B2B]/60 max-w-xs">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-28 border-t border-black/5" data-testid="landing-pricing-section">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">
            {t("landing.pricingTitle")}
          </h2>
          <p className="text-base sm:text-lg text-[#2B2B2B]/60">{t("landing.pricingSubtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {/* Candidates */}
          <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-8" data-testid="landing-pricing-candidates-card">
            <h3 className="font-heading text-lg font-bold mb-1">{t("landing.pricingCandidatesTitle")}</h3>
            <p className="font-heading text-3xl font-bold text-[#4ba3b7] mb-4">{t("landing.pricingCandidatesPrice")}</p>
            <ul className="space-y-2.5 mb-6">
              {CANDIDATE_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[#2B2B2B]/70">
                  <CheckCircle2 className="h-4 w-4 text-[#34C759] shrink-0" /> {t(`landing.${f}`)}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#2B2B2B]/50">{t("landing.pricingCandidatesDesc")}</p>
          </div>

          {/* Producers - Simple offer */}
          <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-8" data-testid="landing-pricing-producers-card">
            <h3 className="font-heading text-lg font-bold mb-1">{t("landing.pricingProducersTitle")}</h3>
            <p className="font-heading text-3xl font-bold text-[#4ba3b7] mb-4">{t("landing.pricingProducersPrice")}</p>
            <ul className="space-y-2.5">
              {PRODUCER_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[#2B2B2B]/70">
                  <CheckCircle2 className="h-4 w-4 text-[#34C759] shrink-0" /> {t(`landing.${f}`)}
                </li>
              ))}
            </ul>
          </div>

          {/* Diffusion Premium */}
          <div className="bg-[#2B2B2B] text-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.1)] p-8 relative overflow-hidden" data-testid="landing-pricing-premium-card">
            <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-[#dde165]/20" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-4 w-4 text-[#dde165]" />
                <h3 className="font-heading text-lg font-bold">{t("landing.pricingPremiumTitle")}</h3>
              </div>
              <p className="font-heading text-3xl font-bold text-[#dde165] mb-4">{t("landing.pricingPremiumPrice")}</p>
              <ul className="space-y-2.5">
                {PREMIUM_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                    <CheckCircle2 className="h-4 w-4 text-[#dde165] shrink-0" /> {t(`landing.${f}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Credits */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-8 sm:p-10">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="h-5 w-5 text-[#4ba3b7]" />
            <h3 className="font-heading text-xl font-bold">{t("landing.pricingCreditsTitle")}</h3>
          </div>
          <p className="text-sm text-[#2B2B2B]/60 max-w-2xl mb-8">{t("landing.pricingCreditsDesc")}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {CREDIT_PACKS.map((pack, i) => (
              <div key={pack.credits} className={`rounded-xl border p-5 text-center ${i === 3 ? "border-[#4ba3b7] ring-1 ring-[#4ba3b7]/30" : "border-black/10"}`} data-testid={`landing-credit-pack-${pack.credits}`}>
                <p className="font-heading text-2xl font-bold text-[#4ba3b7]">{pack.credits}</p>
                <p className="text-xs text-[#2B2B2B]/50 mb-2">{t("common.credits")}</p>
                <p className="font-bold">{pack.price}&euro;</p>
                <p className="text-xs text-[#2B2B2B]/40">{(pack.price / pack.credits).toFixed(2)}&euro;{t("credits.perRecruitmentSuffix")}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <Button asChild size="lg" className="h-14 px-8 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] text-white font-bold text-base" data-testid="landing-pricing-cta-button">
            <Link to="/register?role=producer">{t("landing.pricingCta")} <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
